import React, { useEffect, useState } from "react";
import { X, Save, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { CATEGORIES, INCOME_CATEGORIES, CATEGORY_COLORS } from "../lib/constants";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function TransactionSheet({ open, onClose, initial, onSaved, prefill }) {
  const isEdit = Boolean(initial?.id);
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Outros");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const { api } = useAuth();

  useEffect(() => {
    if (!open) return;
    const src = initial || prefill || {};
    setType(src.type || "expense");
    setAmount(src.amount ? String(src.amount) : "");
    setDescription(src.description || "");
    setCategory(src.category || (src.type === "income" ? "Trabalho" : "Outros"));
    setNotes(src.notes || "");
    setDate(src.date ? src.date.slice(0, 10) : new Date().toISOString().slice(0, 10));
  }, [open, initial, prefill]);

  if (!open) return null;

  const availableCats = type === "income" ? INCOME_CATEGORIES : CATEGORIES;

  const save = async () => {
    const value = parseFloat(String(amount).replace(",", "."));
    if (!value || value <= 0) { toast.error("Informe um valor válido"); return; }
    if (!description.trim()) { toast.error("Informe uma descrição"); return; }
    setSaving(true);
    try {
      const payload = {
        type, amount: value, description: description.trim(),
        category: availableCats.includes(category) ? category : (type === "income" ? "Trabalho" : "Outros"),
        notes, date: new Date(date).toISOString(),
      };
      const r = isEdit
        ? await api.put(`/transactions/${initial.id}`, payload)
        : await api.post("/transactions", payload);
      onSaved(r.data);
      toast.success(isEdit ? "Movimentação atualizada" : "Movimentação registrada");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!isEdit) return;
    if (!window.confirm("Excluir esta movimentação?")) return;
    try {
      await api.delete(`/transactions/${initial.id}`);
      toast.success("Movimentação excluída");
      onSaved(null, initial.id);
      onClose();
    } catch { toast.error("Erro ao excluir"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md animate-slide-up" onClick={onClose}>
      <div
        data-testid="tx-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-gradient-to-b from-slate-900 to-slate-950 border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">{isEdit ? "Editando" : "Nova"}</p>
            <h2 className="font-display text-xl font-bold text-white">{isEdit ? "Editar" : "Nova"} movimentação</h2>
          </div>
          <button data-testid="tx-sheet-close" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 mb-5 p-1 rounded-2xl bg-slate-800/60 border border-white/5">
          <button
            data-testid="tx-type-expense"
            onClick={() => { setType("expense"); if (!CATEGORIES.includes(category)) setCategory("Outros"); }}
            className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-sm font-medium ${type === "expense" ? "bg-rose-500 text-white" : "text-slate-400 hover:text-white"}`}
          ><TrendingDown className="w-4 h-4" /> Saída</button>
          <button
            data-testid="tx-type-income"
            onClick={() => { setType("income"); if (!INCOME_CATEGORIES.includes(category)) setCategory("Trabalho"); }}
            className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-sm font-medium ${type === "income" ? "bg-emerald-500 text-white" : "text-slate-400 hover:text-white"}`}
          ><TrendingUp className="w-4 h-4" /> Entrada</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 font-medium">Valor (R$)</label>
            <input
              data-testid="tx-input-amount"
              inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className="w-full mt-1 px-4 py-3 rounded-xl bg-slate-800/60 border border-white/10 text-white text-2xl font-mono-nums font-bold focus:outline-none focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium">Descrição</label>
            <input
              data-testid="tx-input-description"
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Almoço no restaurante"
              className="w-full mt-1 px-4 py-3 rounded-xl bg-slate-800/60 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium">Categoria</label>
            <div className="mt-2 flex gap-2 overflow-x-auto pnc-scroll-hide">
              {availableCats.map((c) => (
                <button
                  key={c}
                  data-testid={`tx-cat-${c}`}
                  onClick={() => setCategory(c)}
                  className={`shrink-0 px-3.5 py-2 rounded-full text-xs font-medium border ${category === c ? "text-white" : "text-slate-300 hover:text-white"}`}
                  style={{
                    backgroundColor: category === c ? CATEGORY_COLORS[c] + "40" : "rgba(30,41,59,0.6)",
                    borderColor: category === c ? CATEGORY_COLORS[c] : "rgba(255,255,255,0.08)",
                  }}
                >{c}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-medium">Data</label>
              <input
                data-testid="tx-input-date"
                type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="w-full mt-1 px-3 py-3 rounded-xl bg-slate-800/60 border border-white/10 text-white focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium">Notas</label>
              <input
                data-testid="tx-input-notes"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="opcional"
                className="w-full mt-1 px-3 py-3 rounded-xl bg-slate-800/60 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          {isEdit && (
            <button data-testid="tx-delete-btn" onClick={del} className="px-4 py-3 rounded-xl bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Excluir
            </button>
          )}
          <button
            data-testid="tx-save-btn"
            onClick={save} disabled={saving}
            className="btn-emerald flex-1 py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
